import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {
    TableRowActionsMenuItem,
    TableRowActionsToolbarItem,
} from "~/components/table/tableRowActions.types";
import type {EventContainerConstraint} from "~/domainModels/workspaceModules/eventContainersModule";
import type {OttopilotEventContainerWithEvents} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";

const EVENT_CONTAINER_GLOBAL_CONSTRAINT_ALLOWED_ROW_ACTIONS: ReadonlySet<
    TableRowActionsMenuItem | TableRowActionsToolbarItem
> = new Set(["view", "archive", "profile"]);

type EventContainerConstraintEvaluationContext = OttopilotEventContainerWithEvents | Omit<OttopilotEventContainerWithEvents, "events"> & {
    events?: OttopilotEventContainerWithEvents["events"];
};

export type EventContainerEditPanelColumnConfig = {
    isEditable: boolean;
    constraints?: Array<EventContainerConstraint>;
    columnType: "OttopilotEventContainer" | "OttopilotEvent";
    columnId: Uuid | string;
    isAdditionalColumn: boolean;
    eventTemplateId?: Uuid;
};

function getRefValue(
    eventContainer: EventContainerConstraintEvaluationContext,
    refColumnId: Uuid | string,
    isAdditionalColumn: boolean,
    columnType: "OttopilotEventContainer" | "OttopilotEvent",
    eventTemplateId?: Uuid
): string | null | undefined {
    if (columnType === "OttopilotEventContainer") {
        if (isAdditionalColumn === true) {
            return eventContainer.additionalColumnValues[refColumnId as Uuid] as string | null | undefined;
        }
        return (eventContainer as unknown as Record<string, unknown>)[refColumnId] as string | null | undefined;
    }
    const event = eventContainer.events?.find((candidate) => candidate.templateId === eventTemplateId);
    if (event === undefined) {
        return undefined;
    }
    if (isAdditionalColumn === true) {
        return event.additionalColumnValues[refColumnId as Uuid] as string | null | undefined;
    }
    return (event as unknown as Record<string, unknown>)[refColumnId] as string | null | undefined;
}

function isSameCalendarDay(iso8601DateTime: string, now: Date): boolean {
    const d = new Date(iso8601DateTime);
    return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
    );
}

function parseDurationToMs(duration: string): number | null {
    if (duration === "P1D") {
        return 24 * 60 * 60 * 1000;
    }
    const hoursMatch = duration.match(/^PT(\d+)H$/);
    if (hoursMatch !== null) {
        return parseInt(hoursMatch[1]!, 10) * 60 * 60 * 1000;
    }
    return null;
}

function evaluateConstraint(
    eventContainer: EventContainerConstraintEvaluationContext,
    columnConfig: EventContainerEditPanelColumnConfig,
    constraint: EventContainerConstraint,
    now: Date
): boolean {
    if (constraint.type === "Time") {
        const refValue = getRefValue(
            eventContainer,
            constraint.refColumnId,
            constraint.isAdditionalColumn,
            columnConfig.columnType,
            columnConfig.eventTemplateId
        );
        if (refValue === null || refValue === undefined || typeof refValue !== "string") {
            return false;
        }
        if (constraint.duration === "P1D") {
            return isSameCalendarDay(refValue, now);
        }
        const refTime = new Date(refValue).getTime();
        const durationMs = parseDurationToMs(constraint.duration);
        if (durationMs === null) {
            return false;
        }
        return now.getTime() - refTime < durationMs;
    }
    if (constraint.type === "Status") {
        const value = getRefValue(
            eventContainer,
            constraint.refColumnId,
            constraint.isAdditionalColumn,
            constraint.columnType,
            constraint.columnType === "OttopilotEvent" ? columnConfig.eventTemplateId : undefined
        );
        if (value === null || value === undefined) {
            return true;
        }
        const valueUuid = value as Uuid;
        return constraint.disallowedStatuses.includes(valueUuid) === false;
    }
    return true;
}

const eventContainerDetailsConstraintColumnConfig: EventContainerEditPanelColumnConfig = {
    isEditable: true,
    columnType: "OttopilotEventContainer",
    columnId: "id",
    isAdditionalColumn: false,
};

/** Evaluates a single Event Container constraint used by edit panels and schedule-call rules. */
export function evaluateEventContainerConstraint(
    eventContainer: Omit<OttopilotEventContainerWithEvents, "events">,
    constraint: EventContainerConstraint,
    now: Date = new Date(),
): boolean {
    return evaluateConstraint(eventContainer, eventContainerDetailsConstraintColumnConfig, constraint, now);
}

/**
 * Returns effective isEditable for an edit panel column: base isEditable AND all constraints pass.
 */
export function getEditPanelColumnIsEditable(
    eventContainer: OttopilotEventContainerWithEvents,
    columnConfig: EventContainerEditPanelColumnConfig,
    now: Date = new Date(),
    globalConstraints?: Array<EventContainerConstraint>
): boolean {
    if (columnConfig.isEditable !== true) {
        return false;
    }
    const constraints = [
        ...(globalConstraints ?? []),
        ...(columnConfig.constraints ?? []),
    ];
    if (constraints.length === 0) {
        return true;
    }
    return constraints.every((constraint) => evaluateConstraint(eventContainer, columnConfig, constraint, now));
}

export function areEventContainerGlobalConstraintsSatisfied(
    eventContainer: Omit<OttopilotEventContainerWithEvents, "events">,
    globalConstraints?: Array<EventContainerConstraint>,
    now: Date = new Date(),
): boolean {
    if (globalConstraints === undefined || globalConstraints.length === 0) {
        return true;
    }
    return globalConstraints.every((constraint) =>
        evaluateConstraint(eventContainer, eventContainerDetailsConstraintColumnConfig, constraint, now),
    );
}

export function isEventContainerBlockedByGlobalConstraints(
    eventContainer: Omit<OttopilotEventContainerWithEvents, "events">,
    globalConstraints?: Array<EventContainerConstraint>,
    now: Date = new Date(),
): boolean {
    return areEventContainerGlobalConstraintsSatisfied(eventContainer, globalConstraints, now) !== true;
}

export function isEventContainerEngagementRestrictedByGlobalConstraints(
    eventContainer: Omit<OttopilotEventContainerWithEvents, "events">,
    globalConstraints?: Array<EventContainerConstraint>,
    now: Date = new Date(),
): boolean {
    if (globalConstraints === undefined || globalConstraints.length === 0) {
        return false;
    }
    return isEventContainerBlockedByGlobalConstraints(eventContainer, globalConstraints, now);
}

export function isEventContainerRowActionDisabledByGlobalConstraints(
    action: TableRowActionsMenuItem | TableRowActionsToolbarItem,
    eventContainer: Omit<OttopilotEventContainerWithEvents, "events">,
    globalConstraints?: Array<EventContainerConstraint>,
    now: Date = new Date(),
): boolean {
    if (isEventContainerEngagementRestrictedByGlobalConstraints(eventContainer, globalConstraints, now) !== true) {
        return false;
    }
    return EVENT_CONTAINER_GLOBAL_CONSTRAINT_ALLOWED_ROW_ACTIONS.has(action) !== true;
}

export function isListCardFooterRestrictedByGlobalConstraints(
    eventContainer: Omit<OttopilotEventContainerWithEvents, "events">,
    globalConstraints?: Array<EventContainerConstraint>,
    now: Date = new Date(),
): boolean {
    return isEventContainerEngagementRestrictedByGlobalConstraints(eventContainer, globalConstraints, now);
}
